import { Consumer, consumerConverter } from '../models/Consumer'
import { firebaseFuncs, firebaseDb } from './firebase'
import { Functions, Collection, Relationship } from '../config/enums'
import { recipientConverter, Recipient, Address } from '../models/Recipient'

/**
 * Retrieves migrant profile (Consumer object) from Firebase Firestore
 */
export const getProfile = async (userId: string): Promise<Consumer | null> => {
  const doc = await firebaseDb
    .collection(Collection.consumers)
    .doc(userId)
    .withConverter(consumerConverter)
    .get()

  if (doc.exists) {
    return doc.data() as Consumer
  } else {
    return null
  }
}

/**
 * Calls `signupConsumer()` Firebase Function and returns a new `Consumer` object
 */
export const signup = async (
  firstName: string,
  lastName: string,
  avatarUrl?: string,
  phonenumber?: string
): Promise<Consumer | null> => {
  let params: any = { firstName, lastName }
  if (avatarUrl) {
    params.avatarUrl = avatarUrl
  }
  if (phonenumber) {
    params.phonenumber = phonenumber
  }

  const signupFunc = firebaseFuncs.httpsCallable(Functions.signupConsumer)
  const res = await signupFunc(params)
  console.log('signupConsumer res:', res)

  if (res.data) {
    return res.data.consumer as Consumer
  } else {
    return null
  }
}

/**
 * Calls `updateConsumer()` Firebase Function and returns a new `Consumer` object
 */
export const updateConsumer = async (
  firstName?: string,
  lastName?: string,
  avatarUrl?: string,
  phonenumber?: string,
  email?: string,
  homeCountryId?: string,
  workCountryId?: string,
  address?: Address
): Promise<Consumer | null> => {
  const updateConsumerFunc = firebaseFuncs.httpsCallable(
    Functions.updateConsumer
  )

  try {
    let params: any = {}
    if (firstName) {
      params.firstName = firstName
    }
    if (lastName) {
      params.lastName = lastName
    }
    if (avatarUrl) {
      params.avatarUrl = avatarUrl
    }
    if (phonenumber) {
      params.phonenumber = phonenumber
    }
    if (email) {
      params.email = email
    }
    if (homeCountryId) {
      params.homeCountryId = homeCountryId
    }
    if (workCountryId) {
      params.workCountryId = workCountryId
    }
    if (address) {
      params.address = address
    }

    const res = await updateConsumerFunc(params)
    console.log('updateConsumer res:', res)

    if (res.data) {
      return res.data.consumer as Consumer
    } else {
      return null
    }
  } catch (err) {
    console.error(err)
    return null
  }
}

export const updateWorkPermit = async (workPermitUrl: string) => {
  const updateConsumerWorkPermitFunc = firebaseFuncs.httpsCallable(
    Functions.updateConsumerWorkPermit
  )

  try {
    let params: any = {}
    if (workPermitUrl) {
      params.workPermitUrl = workPermitUrl
    }

    await updateConsumerWorkPermitFunc(params)
  } catch (err) {
    console.error(err)
  }
}

export const checkWorkPermit = async (userId: string) => {
  const permit = await firebaseDb
    .collection(Collection.consumers)
    .doc(userId)
    .get()

  return permit.data()?.workPermitUrl ? true : false
}

export const getRecipients = async (userId: string, limit?: number) => {
  const query = firebaseDb
    .collection(Collection.recipients)
    .where('consumerId', '==', userId)
    .orderBy('createdAt')
    .withConverter(recipientConverter)

  if (limit) {
    query.limit(1)
  }

  const snapshot = await query.get()
  const recipients: Recipient[] = []

  snapshot.forEach(doc => {
    if (doc.data().relationship !== Relationship.self) {
      recipients.push(doc.data())
    }
  })

  return recipients
}

export const getRecipient = async (id: string) => {
  const snapshot = await firebaseDb
    .collection(Collection.recipients)
    .doc(id)
    .withConverter(recipientConverter)
    .get()

  return snapshot.data()
}

export const getSelfRecipient = async (userId: string) => {
  const snapshot = await firebaseDb
    .collection(Collection.recipients)
    .where('consumerId', '==', userId)
    .where('relationship', '==', Relationship.self)
    .withConverter(recipientConverter)
    .limit(1)
    .get()

  return snapshot.size ? snapshot.docs[0].data() : undefined
}

export const createRecipient = async (recipient: Recipient) => {
  const createRecipientFunc = firebaseFuncs.httpsCallable(
    Functions.createRecipient
  )

  delete recipient.id
  const res = await createRecipientFunc(recipient)

  return res.data.recipientId
}

export const updateRecipient = async (recipient: Recipient) => {
  const updateRecipientFunc = firebaseFuncs.httpsCallable(
    Functions.updateRecipient
  )

  await updateRecipientFunc(recipient)
}

export const deleteRecipient = async (id: string) => {
  const deleteRecipientFunc = firebaseFuncs.httpsCallable(
    Functions.deleteRecipient
  )

  await deleteRecipientFunc({ id })
}
